import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Alert, alpha, Box, Container, Typography } from '@mui/material';

import FullPageLoader from './FullPageLoader';
import AmbientBackground from './AmbientBackground';
import { getClerkPublishableKey } from './clerkPublishableKey';
import PremiumPricingBlock from './PremiumPricingBlock';
import {
  createRobokassaPayment,
  getAccountWebSubscription,
  getLettWorkerBaseUrl,
  redirectToRobokassa,
} from './webSubscription';

function formatRuLongDate(date) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

export default function Account() {
  const publishableKey = getClerkPublishableKey();

  if (!publishableKey) {
    return <Navigate to="/" replace />;
  }

  return <AccountWithClerk />;
}

function AccountWithClerk() {
  const { t } = useTranslation();
  const { isLoaded, isSignedIn, sessionId } = useAuth();
  const { user } = useUser();
  const accountSceneRef = useRef(null);
  const [paymentError, setPaymentError] = useState('');
  const [paymentInfo, setPaymentInfo] = useState('');
  const [paymentPendingMonths, setPaymentPendingMonths] = useState(null);

  const workerBaseUrl = getLettWorkerBaseUrl();
  const workerConfigured = Boolean(workerBaseUrl);

  const subscription = useMemo(() => getAccountWebSubscription(user), [user]);
  const premiumAccess = subscription?.isActive ? subscription : null;
  const expiredAccess = subscription && !subscription.isActive ? subscription : null;
  const searchParams = useMemo(() => {
    if (typeof window === 'undefined') return new URLSearchParams();
    return new URLSearchParams(window.location.search);
  }, []);

  useEffect(() => {
    const paymentState = searchParams.get('payment');
    if (!paymentState) return;

    if (paymentState === 'success') {
      setPaymentInfo(t('account.paymentSuccess'));
      setPaymentError('');

      if (user?.reload) {
        let cancelled = false;
        const delays = [300, 1200, 2500];

        delays.forEach((delay) => {
          window.setTimeout(() => {
            if (!cancelled) {
              user.reload().catch((error) => console.error('Failed to reload Clerk user:', error));
            }
          }, delay);
        });

        return () => {
          cancelled = true;
        };
      }
    }

    if (paymentState === 'fail') {
      setPaymentInfo('');
      setPaymentError(t('account.paymentFailed'));
    }

    return undefined;
  }, [searchParams, t, user]);

  useEffect(() => {
    const scene = accountSceneRef.current;
    if (!scene || typeof window === 'undefined') return undefined;

    let frame = 0;

    const render = () => {
      frame = 0;
      const scrollRatio = Math.min(window.scrollY / Math.max(window.innerHeight, 1), 1.5);
      scene.style.setProperty('--ambient-scroll', scrollRatio.toFixed(4));
    };

    const requestRender = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(render);
    };

    window.addEventListener('scroll', requestRender, { passive: true });
    window.addEventListener('resize', requestRender);
    requestRender();

    return () => {
      window.removeEventListener('scroll', requestRender);
      window.removeEventListener('resize', requestRender);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, []);

  const handlePayPlan = useCallback(
    async (planMonths) => {
      if (!user?.id || !sessionId) {
        setPaymentError(t('account.paymentAuthError'));
        return;
      }

      setPaymentPendingMonths(planMonths);
      setPaymentError('');
      setPaymentInfo('');

      try {
        const { paymentParams } = await createRobokassaPayment({
          clerkUserId: user.id,
          clerkSessionId: sessionId,
          email: user.primaryEmailAddress?.emailAddress || '',
          planMonths,
        });

        redirectToRobokassa(paymentParams);
      } catch (error) {
        console.error('Failed to start payment:', error);
        setPaymentError(error.message || t('account.paymentUnknownError'));
      } finally {
        setPaymentPendingMonths(null);
      }
    },
    [sessionId, t, user]
  );

  if (!isLoaded) {
    return <FullPageLoader />;
  }

  if (!isSignedIn) {
    return <Navigate to="/" replace />;
  }

  return (
    <Box ref={accountSceneRef} className="home-scene" sx={{ position: 'relative', isolation: 'isolate', minHeight: '100vh' }}>
      <AmbientBackground />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Container maxWidth="md" sx={{ padding: 3, paddingTop: 4, paddingBottom: 6 }}>
          <Box
            sx={{
              mb: 4,
              display: 'flex',
              gap: 3,
              alignItems: 'center',
              flexDirection: { xs: 'column', md: 'row' },
            }}
          >
            <Box
              sx={{
                flex: '1 1 0',
                pr: 3,
                minWidth: 0,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.45 }}>
                {premiumAccess
                  ? t('account.premiumAccessUntil', {
                      date: formatRuLongDate(premiumAccess.activeUntil),
                    })
                  : expiredAccess
                    ? t('account.premiumAccessExpired', {
                        date: formatRuLongDate(expiredAccess.activeUntil),
                      })
                    : t('account.premiumAccessInactive')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, lineHeight: 1.65 }}>
                {premiumAccess
                  ? t('account.premiumAccessAppHint')
                  : expiredAccess
                    ? t('account.premiumAccessAppHintExpired')
                    : t('account.premiumAccessAppHintInactive')}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: { md: '0 0 280px' },
                width: { xs: '100%', md: 280 },
                maxWidth: 360,
                borderRadius: 3,
                overflow: 'hidden',
                position: 'relative',
                transform: 'translateZ(0)',
                bgcolor: alpha('#fff', 0.04),
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                animation: 'gradientGlow 3s ease-in-out infinite',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -2,
                  left: -2,
                  right: -2,
                  bottom: -2,
                  borderRadius: 14,
                  background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #feca57, #ff9ff3)',
                  backgroundSize: '400% 400%',
                  animation: 'colorShift 8s ease-in-out infinite',
                  zIndex: -1,
                  opacity: 0.6,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: 12,
                  background:
                    'linear-gradient(45deg, rgba(255, 107, 107, 0.08), rgba(78, 205, 196, 0.08), rgba(69, 183, 209, 0.08))',
                  backgroundSize: '400% 400%',
                  animation: 'colorShift 6s ease-in-out infinite',
                  zIndex: -1,
                  opacity: 0.35,
                },
              }}
            >
              <Box
                component="img"
                src="/settings-web.jpg"
                alt={t('account.settingsScreenshotAlt')}
                sx={{
                  display: 'block',
                  width: '100%',
                  height: 'auto',
                  transform: 'translateZ(0)',
                }}
              />
            </Box>
          </Box>

          {paymentInfo && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {paymentInfo}
            </Alert>
          )}

          {paymentError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {paymentError}
            </Alert>
          )}

          {!premiumAccess && (
            <PremiumPricingBlock
              variant="account"
              onPayPlan={handlePayPlan}
              paymentPendingMonths={paymentPendingMonths}
              paymentDisabled={!workerConfigured}
            />
          )}
        </Container>
      </Box>

      {paymentPendingMonths != null && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 20000,
            bgcolor: 'background.default',
          }}
        >
          <FullPageLoader />
        </Box>
      )}
    </Box>
  );
}
